import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animate, motion, useMotionValue, useReducedMotion } from 'framer-motion';
interface PageCountSliderProps {
  steps: string[];
  activeIndex: number;
  onChange: (index: number) => void;
}
export function PageCountSlider({
  steps,
  activeIndex,
  onChange
}: PageCountSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const x = useMotionValue(0);
  const prefersReducedMotion = useReducedMotion();
  const lastStepCount = steps.length - 1;
  useLayoutEffect(() => {
    const measure = () => setTrackWidth(trackRef.current?.offsetWidth ?? 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  useEffect(() => {
    if (!trackWidth) return;
    const target = activeIndex / lastStepCount * trackWidth;
    const controls = animate(x, target, prefersReducedMotion ? {
      duration: 0
    } : {
      type: 'spring',
      stiffness: 420,
      damping: 34
    });
    return () => controls.stop();
  }, [activeIndex, trackWidth, lastStepCount, prefersReducedMotion, x]);
  const handleDragEnd = () => {
    if (!trackWidth) return;
    const raw = x.get();
    const nearest = Math.round(raw / trackWidth * lastStepCount);
    const clamped = Math.min(Math.max(nearest, 0), lastStepCount);
    onChange(clamped);
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onChange(Math.max(activeIndex - 1, 0));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onChange(Math.min(activeIndex + 1, lastStepCount));
    }
  };
  return <div className="w-full" data-id="element-516" data-mp="bc0ja8zd5h90">
      <div className="flex items-center justify-between" data-id="element-517" data-mp="bap65ik12x58">
        {steps.map((step, index) => <button key={step} type="button" onClick={() => onChange(index)} aria-pressed={index === activeIndex} className={`cursor-pointer rounded-sm py-1 text-[13px] underline decoration-1 underline-offset-4 transition-colors duration-150 ease-out outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${index === activeIndex ? 'font-medium text-ink decoration-ink' : 'text-muted decoration-transparent hover:text-ink hover:decoration-hairline'}`} data-id="element-518" data-mp="1royuwds6mnwl" data-mpi={index}>
            {step}
          </button>)}
      </div>

      <div ref={trackRef} className="relative mt-4 h-px w-full bg-hairline" data-id="element-519" data-mp="1g4a5dzaeukjr">
        {steps.map((step, index) => <span key={step} aria-hidden="true" className="absolute top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-hairline" style={{
        left: `${index / lastStepCount * 100}%`
      }} data-id="element-520" data-mp="1l1jtnbm2cq60" data-mpi={index} />)}

        <motion.div role="slider" tabIndex={0} aria-label="Select page count" aria-orientation="horizontal" aria-valuemin={0} aria-valuemax={lastStepCount} aria-valuenow={activeIndex} aria-valuetext={steps[activeIndex]} drag="x" dragConstraints={{
        left: 0,
        right: trackWidth
      }} dragElastic={0.04} dragMomentum={false} onDragEnd={handleDragEnd} onKeyDown={handleKeyDown} style={{
        x
      }} className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full bg-ink outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-accent active:cursor-grabbing" data-id="element-521" data-mp="xbz76iqpbarz" />
      </div>
    </div>;
}